import React, { useState, useEffect } from 'react';
import './ProductCard.css';
import Swal from 'sweetalert2';
import Loader from '../../../components/helpers/Loader';

function ProductCard({ product, onEdit, onDelete }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedProduct, setEditedProduct] = useState(product);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Update local state when product prop changes
  useEffect(() => {
    // Ensure features is always an array for editing
    const productWithArrayFeatures = {
      ...product,
      features: Array.isArray(product.features) 
        ? product.features 
        : (typeof product.features === 'string' && product.features)
          ? product.features.split(/[,\n]/).map(f => f.trim()).filter(f => f.length > 0)
          : []
    };
    setEditedProduct(productWithArrayFeatures);
  }, [product]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.keyCode === 27 && isEditMode) {
        handleCancelEdit();
      }
    };

    if (isEditMode) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open and add modal class
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [isEditMode]);

  // Use the product prop directly for display instead of local state
  const displayProduct = product;
  
  let featuresArray = [];
  
  try {
    if (displayProduct.features) {
      if (Array.isArray(displayProduct.features)) {
        featuresArray = displayProduct.features;
      } else if (typeof displayProduct.features === 'string') {
        try {
          featuresArray = JSON.parse(displayProduct.features);
        } catch {
          featuresArray = displayProduct.features
            .split(/[,\n]/)
            .map(f => f.trim())
            .filter(f => f.length > 0);
        }
      }
    }
  } catch (e) {
    console.error('Error processing features:', e);
    featuresArray = [];
  }

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    
    try {
      // Clean up features - remove empty strings and trim
      const cleanedFeatures = Array.isArray(editedProduct.features) 
        ? editedProduct.features.map(f => f.trim()).filter(f => f.length > 0)
        : [];
      
      // Ensure we have the product ID
      const productToUpdate = { 
        ...editedProduct, 
        id: displayProduct._id,
        features: cleanedFeatures
      };        //MongoDB uses _id
      //const productToUpdate = { ...editedProduct, id: displayProduct.id };       //Other MYSQL use id
      
      const updatedProduct = await onEdit(productToUpdate);
      
      // Validate the response
      if (!updatedProduct || typeof updatedProduct !== 'object') {
        throw new Error('Invalid response from server');
      }

      // Update the edited product with the latest data
      setEditedProduct(updatedProduct);
      setIsEditMode(false);
      
      await Swal.fire({
        title: 'Success!',
        text: 'Product has been updated successfully',
        icon: 'success',
        confirmButtonColor: '#28a745',
        confirmButtonText: 'OK'
      });
      
    } catch (err) {
      console.error('Save error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update product';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset with proper features array structure
    const productWithArrayFeatures = {
      ...product,
      features: Array.isArray(product.features) 
        ? product.features 
        : (typeof product.features === 'string' && product.features)
          ? product.features.split(/[,\n]/).map(f => f.trim()).filter(f => f.length > 0)
          : []
    };
    setEditedProduct(productWithArrayFeatures);
    setError('');
  };

  // Don't render if displayProduct is invalid
  if (!displayProduct || !displayProduct.name) {
    return (
      <div className="product-card">
        <div className="product-header">
          <div className="loading-container">
            <Loader size={24} color="#4f46e5" />
            <span className="loading-text">Loading product...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="product-card">
        <div className="product-header">
          <h2 className="product-title">{displayProduct.name}</h2>
          {displayProduct.isPopular && <span className="popular-badge">Popular</span>}
        </div>
        
        <div className="product-pricing">
          <span className="price">${displayProduct.price || 0}</span>
          <span className="period">/month</span>
          {displayProduct.annualPrice && (
            <p className="annual-price">${displayProduct.annualPrice} when billed annually</p>
          )}
        </div>
        
        {displayProduct.description && (
          <p className="product-description">{displayProduct.description}</p>
        )}
        
        {featuresArray.length > 0 && (
          <ul className="features-list">
            {featuresArray.map((feature, index) => (
              <li key={index} className="feature-item">
                <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        )}
        
        <div className="product-actions">
          <div className="admin-actions">
            <button 
              className="edit-btn" 
              onClick={() => setIsEditMode(true)}
            >
              Edit
            </button>
            <button 
              className="delete-btn" 
              onClick={async () => {
                const result = await Swal.fire({
                  title: 'Are you sure?',
                  text: "You won't be able to revert this!",
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: '#28a745',
                  cancelButtonColor: '#d33',
                  confirmButtonText: 'Yes, delete it!'
                });

                if (result.isConfirmed) {
                  setIsDeleting(true);
                  try {
                    await onDelete(displayProduct._id);       //MongoDB uses _id
                    //await onDelete(displayProduct.id);          // MYSQL  use id
                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Product has been deleted successfully',
                      icon: 'success',
                      confirmButtonColor: '#28a745',
                      confirmButtonText: 'OK'
                    });
                  } catch (err) {
                    const errorMessage = err.response?.data?.message || err.message || 'Failed to delete product';
                    setError(errorMessage);
                    console.error('Delete error:', err);
                  } finally {
                    setIsDeleting(false);
                  }
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="button-loading">
                  <Loader size={16} color="#ffffff" />
                  <span>Deleting...</span>
                </span>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Form Modal */}
      {isEditMode && (
        <div 
          className="edit-form-container"
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              handleCancelEdit();
            }
          }}
        >
          <div className="edit-form-wrapper">
            <div className="edit-form-header">
              <h3 className="edit-form-title">Edit Product</h3>
              <button 
                type="button" 
                className="close-edit-btn" 
                onClick={handleCancelEdit}
                disabled={isSaving}
                aria-label="Close edit form"
              >
                ✕
              </button>
            </div>
            
            <form className="edit-form" onSubmit={handleSaveEdit}>
              <div className="form-grid">
                  {/* Basic Information Section */}
                  <div className="form-section">
                    <h4 className="section-title">Basic Information</h4>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="product-name">
                        Product Name <span className="required">*</span>
                      </label>
                      <input
                        id="product-name"
                        type="text"
                        className="form-input"
                        value={editedProduct.name || ''}
                        onChange={(e) => setEditedProduct({...editedProduct, name: e.target.value})}
                        placeholder="Enter product name"
                        required
                        disabled={isSaving}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="product-description">
                        Description
                      </label>
                      <textarea
                        id="product-description"
                        className="form-textarea"
                        value={editedProduct.description || ''}
                        onChange={(e) => setEditedProduct({...editedProduct, description: e.target.value})}
                        placeholder="Enter product description"
                        rows="4"
                        disabled={isSaving}
                      />
                    </div>
                  </div>

                  {/* Pricing Section */}
                  <div className="form-section">
                    <h4 className="section-title">Pricing</h4>
                    
                    <div className="form-row">
                      <div className="form-group form-group-half">
                        <label className="form-label" htmlFor="monthly-price">
                          Monthly Price <span className="required">*</span>
                        </label>
                        <div className="input-with-currency">
                          <span className="currency-symbol">$</span>
                          <input
                            id="monthly-price"
                            type="number"
                            className="form-input currency-input"
                            value={editedProduct.price != null ? editedProduct.price : ''}
                            onChange={(e) => setEditedProduct({...editedProduct, price: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                            placeholder="0.00"
                            required
                            step="0.01"
                            min="0"
                            disabled={isSaving}
                          />
                        </div>
                      </div>

                      <div className="form-group form-group-half">
                        <label className="form-label" htmlFor="annual-price">
                          Annual Price
                        </label>
                        <div className="input-with-currency">
                          <span className="currency-symbol">$</span>
                          <input
                            id="annual-price"
                            type="number"
                            className="form-input currency-input"
                            value={editedProduct.annualPrice != null ? editedProduct.annualPrice : ''}
                            onChange={(e) => setEditedProduct({...editedProduct, annualPrice: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            disabled={isSaving}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Features Section */}
                  <div className="form-section features-section">
                    <div className="section-header">
                      <h4 className="section-title">Features</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const currentFeatures = Array.isArray(editedProduct.features) ? editedProduct.features : [];
                          setEditedProduct({...editedProduct, features: [...currentFeatures, '']});
                        }}
                        disabled={isSaving}
                        className="add-feature-btn-header"
                      >
                        <span className="plus-icon">+</span>
                        Add Feature
                      </button>
                    </div>
                    
                    <div className="features-management">
                      {(Array.isArray(editedProduct.features) ? editedProduct.features : []).length === 0 ? (
                        <div className="no-features-message">
                          <p>No features added yet. Click "Add Feature" to start adding features.</p>
                        </div>
                      ) : (
                        (Array.isArray(editedProduct.features) ? editedProduct.features : []).map((feature, index) => (
                          <div key={index} className="feature-input-row">
                            <div className="feature-number">{index + 1}</div>
                            <input
                              type="text"
                              className="feature-input"
                              value={feature || ''}
                              onChange={(e) => {
                                const newFeatures = [...(Array.isArray(editedProduct.features) ? editedProduct.features : [])];
                                newFeatures[index] = e.target.value;
                                setEditedProduct({...editedProduct, features: newFeatures});
                              }}
                              placeholder="Enter feature description"
                              disabled={isSaving}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newFeatures = (Array.isArray(editedProduct.features) ? editedProduct.features : []).filter((_, i) => i !== index);
                                setEditedProduct({...editedProduct, features: newFeatures});
                              }}
                              disabled={isSaving}
                              className="remove-feature-btn"
                              title="Remove this feature"
                              aria-label={`Remove feature ${index + 1}`}
                            >
                              <span className="delete-icon">🗑️</span>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Additional Options */}
                  {/* <div className="form-section">
                    <h4 className="section-title">Options</h4>
                    
                    { <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={editedProduct.isPopular || false}
                          onChange={(e) => setEditedProduct({...editedProduct, isPopular: e.target.checked})}
                          disabled={isSaving}
                        />
                        <span className="checkbox-text">Mark as Popular</span>
                        <span className="checkbox-description">This will display a "Popular" badge on the product card</span>
                      </label>
                    </div> }
                  </div> */}
                </div>

                {error && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <span className="error-text">{error}</span>
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={isSaving}>
                    {isSaving ? (
                      <span className="button-loading">
                        <Loader size={16} color="#ffffff" />
                        <span>Saving...</span>
                      </span>
                    ) : (
                      <>
                        <span className="save-icon">💾</span>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }

  export default ProductCard;